import { useState, useEffect, useCallback, useRef } from 'react';
import { telemetryService, VideoTelemetryData } from '@/lib/telemetry/telemetryService';
import { useTelemetryHUD as useTelemetryHUDStore } from '@/state/telemetryHUD';
import { TelemetryFrame } from '@/components/TelemetryHUD';

export interface UseTelemetryHUDOptions {
  /** Video ID to load telemetry for */
  videoId?: string;
  /** Whether to auto-load telemetry when videoId changes */
  autoLoad?: boolean;
  /** Minimum frames to buffer before ready */
  minFrames?: number;
}

export interface UseTelemetryHUDReturn {
  /** Whether telemetry data is available */
  hasTelemetryData: boolean;
  /** Whether telemetry is currently loading */
  isLoading: boolean;
  /** Error message if loading failed */
  error: string | null;
  /** Function to get telemetry frame at specific time */
  getFrameAtTime: (timeSec: number) => TelemetryFrame | null;
  /** HUD visibility state */
  isVisible: boolean;
  /** Toggle HUD visibility */
  toggleVisible: () => void;
  /** Set HUD visibility */
  setVisible: (visible: boolean) => void;
  /** HUD opacity */
  opacity: number;
  /** Set HUD opacity */
  setOpacity: (opacity: number) => void;
  /** Manually load telemetry for current video */
  loadTelemetry: () => Promise<void>;
  /** Dispose of current telemetry data */
  dispose: () => void;
}

/**
 * Hook for managing telemetry HUD state and data loading
 */
export function useTelemetryHUD(options: UseTelemetryHUDOptions = {}): UseTelemetryHUDReturn {
  const { videoId, autoLoad = true, minFrames = 30 } = options;
  
  // Local state for telemetry data
  const [telemetryData, setTelemetryData] = useState<VideoTelemetryData>({
    available: false,
    reader: null,
    error: null,
    loading: false,
  });
  
  // Global HUD state
  const {
    isVisible,
    opacity,
    setVisible,
    toggleVisible,
    setOpacity,
  } = useTelemetryHUDStore();
  
  // Keep track of current video ID to avoid stale data
  const currentVideoIdRef = useRef<string | undefined>(videoId);
  
  // Update current video ID ref
  useEffect(() => {
    currentVideoIdRef.current = videoId;
  }, [videoId]);
  
  // Function to get telemetry frame at specific time
  const getFrameAtTime = useCallback((timeSec: number): TelemetryFrame | null => {
    if (!telemetryData.reader || !telemetryData.available) {
      return null;
    }
    
    try {
      const frame = telemetryData.reader.getFrameAt(timeSec);
      return frame as TelemetryFrame | null;
    } catch (error) {
      console.error('[useTelemetryHUD] Error getting frame at time:', error);
      return null;
    }
  }, [telemetryData.reader, telemetryData.available]);
  
  // Load telemetry data
  const loadTelemetry = useCallback(async () => {
    if (!videoId) {
      setTelemetryData({
        available: false,
        reader: null,
        error: 'No video ID provided',
        loading: false,
      });
      return;
    }
    
    try {
      setTelemetryData(prev => ({ ...prev, loading: true, error: null }));
      
      const data = await telemetryService.getTelemetryForVideo(videoId, {
        minFrames,
      });
      
      // Check if we're still on the same video (avoid race conditions)
      if (currentVideoIdRef.current === videoId) {
        // S-2225: Enhanced error messaging for better UX
        let errorMessage = data.error;
        if (data.error) {
          // Provide user-friendly messages for common issues
          if (data.error.includes('column') && data.error.includes('does not exist')) {
            errorMessage = 'Telemetry data format not supported for this video';
          } else if (data.error.includes('No telemetry data available')) {
            errorMessage = 'No telemetry yet - analysis may still be processing';
          } else if (data.error.includes('Database error')) {
            errorMessage = 'Unable to load telemetry data at this time';
          }
        }

        setTelemetryData({
          available: data.available,
          reader: data.reader,
          error: errorMessage,
          loading: false,
        });
      }
    } catch (error) {
      console.error('[useTelemetryHUD] Error loading telemetry:', error);
      
      // Check if we're still on the same video
      if (currentVideoIdRef.current === videoId) {
        // S-2225: Provide friendly error messages
        let errorMessage = 'Unable to load telemetry data';
        if (error instanceof Error) {
          if (error.message.includes('42703') || error.message.includes('column') && error.message.includes('does not exist')) {
            errorMessage = 'Telemetry data format not supported';
          } else if (error.message.includes('network') || error.message.includes('fetch')) {
            errorMessage = 'Network error - please try again';
          } else {
            errorMessage = error.message;
          }
        }

        setTelemetryData({
          available: false,
          reader: null,
          error: errorMessage,
          loading: false,
        });
      }
    }
  }, [videoId, minFrames]);
  
  // Dispose of current telemetry data
  const dispose = useCallback(() => {
    if (videoId) {
      telemetryService.disposeTelemetry(videoId);
    }
    setTelemetryData({
      available: false,
      reader: null,
      error: null,
      loading: false,
    });
  }, [videoId]);
  
  // Auto-load telemetry when video ID changes
  useEffect(() => {
    if (autoLoad && videoId) {
      loadTelemetry();
    } else if (!videoId) {
      // Clear data when no video ID
      setTelemetryData({
        available: false,
        reader: null,
        error: null,
        loading: false,
      });
    }
    
    // Cleanup on unmount or video change
    return () => {
      if (videoId && currentVideoIdRef.current !== videoId) {
        telemetryService.disposeTelemetry(videoId);
      }
    };
  }, [videoId, autoLoad, loadTelemetry]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (currentVideoIdRef.current) {
        telemetryService.disposeTelemetry(currentVideoIdRef.current);
      }
    };
  }, []);
  
  return {
    hasTelemetryData: telemetryData.available,
    isLoading: telemetryData.loading,
    error: telemetryData.error,
    getFrameAtTime,
    isVisible,
    toggleVisible,
    setVisible,
    opacity,
    setOpacity,
    loadTelemetry,
    dispose,
  };
}

export default useTelemetryHUD;
